import DateRangeSelector from '@/components/DateRangeSelector'
import Paper from '@/components/Paper'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, TextField, Typogaphy } from 'vnetwork-ui'
import VNISCustomerAnalytics from '../templates/VNISCustomerAnalytics'
import { AnalyticQuery } from '../types'
import { DataBuilderConfig } from '../utils/functions'
import DataVisualization from './DataVisualization'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import { useForm } from 'react-hook-form'
import { DEPARTMENT_IDS } from '@/constants/departments'

// Display mode configuration
export type DisplayMode = 'default' | 'detailed' | 'two-line'

export interface DisplayConfig {
    mode: DisplayMode
    showOriginalValues: boolean
    showConversionInfo: boolean
    showGrowthIndicators: boolean
    showAllProviders: boolean
    providers: string[]
    selectedProviders: string[] // Array of selected provider names to display
}

const today = dayjs().format('YYYY-MM-DD')
interface ServiceAnalyticProps {
    productGroupName: string
}
const ServiceAnalytic = ({ productGroupName }: ServiceAnalyticProps) => {
    const { t } = useTranslation()

    const { control, watch } = useForm({
        defaultValues: {
            account_manager_codes: [],
        },
    })

    const [queries, setQueries] = useState<AnalyticQuery>({
        gte: dayjs().subtract(1, 'month').add(1, 'day').format('YYYY-MM-DD'),
        lte: today,
        account_manager_codes: [],
        sale_manager_codes: [],
        product_group_name: productGroupName,
        q: '',
    })

    // Analytics configuration
    const [analyticsConfig, _setAnalyticsConfig] = useState<
        Omit<DataBuilderConfig, 'startDate' | 'endDate'>
    >({
        timePeriod: 'weekly',
        aggregationMethod: 'sum',
        usageKeys: undefined,
    })

    // Display configuration
    const [displayConfig, setDisplayConfig] = useState<DisplayConfig>({
        mode: 'default',
        showOriginalValues: true,
        showConversionInfo: true,
        showGrowthIndicators: true,
        showAllProviders: false,
        providers: [],
        selectedProviders: [], // Default selected providers
    })

    // Available providers (will be populated from data)

    // Handle providers loaded from data
    const displayModeOptions = [
        {
            value: 'default',
            label: t('services.analytics.display_mode.default'),
        },
        {
            value: 'detailed',
            label: t('services.analytics.display_mode.detailed'),
        },
        {
            value: 'two-line',
            label: t('services.analytics.display_mode.two_line'),
        },
    ]

    // Update product_group_name when productGroupName prop changes
    useEffect(() => {
        setQueries((prev) => ({
            ...prev,
            product_group_name: productGroupName,
        }))
    }, [productGroupName])

    useEffect(() => {
        setQueries((prev) => ({
            ...prev,
            account_manager_codes: watch('account_manager_codes'),
        }))
    }, [watch('account_manager_codes')])

    return (
        <div className="space-y-2">
            <Paper>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="col-span-1 md:col-span-3">
                        <TextField
                            label={t('search')}
                            size="small"
                            value={queries.q}
                            onChange={(e) =>
                                setQueries({ ...queries, q: e.target.value })
                            }
                            placeholder={t('enter_search')}
                        />
                    </div>
                    <div className="col-span-1 md:col-span-3">
                        <EmployeeSelect
                            control={control}
                            multiple
                            name="account_manager_codes"
                            departmentIds={[DEPARTMENT_IDS.ACCOUNT]}
                            onlyActive
                            label={t('account_manager_codes')}
                        />
                    </div>
                    <div className="col-span-1 md:col-span-3">
                        <Select // for display mode
                            options={displayModeOptions}
                            value={displayConfig.mode}
                            onChange={(value) =>
                                setDisplayConfig({
                                    ...displayConfig,
                                    mode: value as DisplayMode,
                                })
                            }
                        />
                    </div>
                    <DateRangeSelector
                        className="col-span-1 md:col-span-3"
                        placement="right"
                        excludeOptions={['today', 'yesterday']}
                        value={{
                            gte: queries.gte || '',
                            lte: queries.lte || '',
                        }}
                        maxDate={dayjs()
                            .subtract(1, 'day')
                            .format('YYYY-MM-DD')}
                        minDate={'2025-09-01'}
                        onChange={(value) =>
                            setQueries({
                                ...queries,
                                gte: value.gte,
                                lte: value.lte,
                            })
                        }
                    />
                </div>
                {productGroupName === 'VNIS' && (
                    <div className="mt-4 space-y-3">
                        {!displayConfig.showAllProviders && (
                            <div>
                                <div className="space-y-3">
                                    <div>
                                        <Typogaphy className="text-xs text-gray-600 mb-2">
                                            {t(
                                                'services.analytics.additional_providers'
                                            )}
                                            :
                                        </Typogaphy>
                                        <Select
                                            multiple
                                            options={displayConfig?.providers?.map(
                                                (provider) => ({
                                                    value: provider,
                                                    label: provider.toUpperCase(),
                                                })
                                            )}
                                            value={
                                                displayConfig?.selectedProviders
                                            }
                                            onChange={(values) =>
                                                setDisplayConfig({
                                                    ...displayConfig,
                                                    selectedProviders:
                                                        values as string[],
                                                })
                                            }
                                            placeholder={t(
                                                'services.analytics.select_additional_providers'
                                            )}
                                        />
                                    </div>
                                </div>
                                <Typogaphy className="text-xs text-gray-500 mt-1">
                                    {t(
                                        'services.analytics.unselected_providers_grouped_as_others'
                                    )}
                                </Typogaphy>
                            </div>
                        )}
                    </div>
                )}
            </Paper>

            <DataVisualization
                query={queries}
                analyticsConfig={analyticsConfig}
                displayConfig={displayConfig}
            />
            {productGroupName === 'VNIS' && (
                <VNISCustomerAnalytics productGroupName={productGroupName} />
            )}
        </div>
    )
}

export default ServiceAnalytic
